import { PUBLICATION } from "@config/seo-sitemap";
import { LabelCardIconVariety } from "@elements/Icon/LabelCardIcon";
import {
  ARTICLE_PAYWALL_SELECTOR,
  ARTICLE_REGWALL_SELECTOR,
  BH_LOGO,
  URL_ORIGIN,
} from "@elements/MetaTags/MetaTagsConstants";
import { removeLeadingSlash } from "@helper/removeTrailingLeadingSlash";
import { ProcessedArticleData } from "@transformer/useOSResponse";

type BreadcrumbItem = {
  position: number;
  name: string;
  item: string;
};

type ArticleSchemaAuthor = {
  name: string;
  url?: string;
};

type ArticleSchemaImage = {
  url: string;
  width?: number;
  height?: number;
};

type ArticleSchemaProps = {
  url: string;
  title: string;
  images: ArticleSchemaImage[];
  datePublished: string;
  dateModified: string;
  authors?: ArticleSchemaAuthor[];
  publisherName: string;
  publisherLogo: {
    url: string;
    width: number;
    height: number;
  };
  description: string;
  accessWall?: {
    cssSelector: string;
  };
  speakable: {
    cssSelector: string[];
  };
  mainEntityOfPage: string;
};

export function getArticleSchemaProps(
  article: ProcessedArticleData,
  description: string,
): {
  breadcrumbItems: BreadcrumbItem[];
  articleSchemaProps: ArticleSchemaProps;
} {
  const { campaignReference, sectionName, uniqueSectionName } = article;
  const isCampaign = Array.isArray(campaignReference)
    ? campaignReference.length > 0
    : !!(campaignReference?.href && campaignReference.title);

  const breadcrumbName = isCampaign
    ? (campaignReference?.title ?? sectionName)
    : sectionName;
  const breadcrumbPath = isCampaign
    ? `mata-mata/${campaignReference?.href.split("/").at(-1)}`
    : (uniqueSectionName || "").replace(/_/g, "/");

  const breadcrumbItems: BreadcrumbItem[] = breadcrumbName
    ? [
        {
          position: 1,
          name: "Home",
          item: URL_ORIGIN,
        },
        {
          position: 2,
          name: breadcrumbName,
          item: `${URL_ORIGIN}${removeLeadingSlash(breadcrumbPath || "")}`,
        },
        {
          position: 3,
          name: article.title,
          item: `${URL_ORIGIN}${removeLeadingSlash(article.urlPath || "")}`,
        },
      ]
    : [];

  const accessWallMap: Record<string, string> = {
    [LabelCardIconVariety.PREMIUM]: ARTICLE_PAYWALL_SELECTOR,
    [LabelCardIconVariety.REGISTERED_USER]: ARTICLE_REGWALL_SELECTOR,
  };
  const accessWallSelector = accessWallMap[article.contentAccess] || "";

  const articleSchemaProps: ArticleSchemaProps = {
    url: `${URL_ORIGIN}${removeLeadingSlash(article.urlPath || "")}`,
    title: article.title,
    images: [article.originalImage, article.landscapeImage, article.squareImage]
      .filter((img): img is NonNullable<typeof img> => Boolean(img?.url))
      .map((img) => ({
        url: `${URL_ORIGIN}${removeLeadingSlash(img.url)}`,
        width: img.width,
        height: img.height,
      })),
    datePublished: article.datetime,
    dateModified: article.dateEdited || article.datetime,
    ...(article.authors?.length && {
      authors: article.authors.map((author) => ({
        name: author.name,
        url: author.urlPath
          ? `${URL_ORIGIN}${removeLeadingSlash(author.urlPath)}`
          : undefined,
      })),
    }),
    publisherName: PUBLICATION,
    publisherLogo: {
      url: `${URL_ORIGIN}${BH_LOGO}`,
      width: 600,
      height: 60,
    },
    description,
    ...(article.contentAccess !== LabelCardIconVariety.FREE && {
      accessWall: {
        cssSelector: accessWallSelector,
      },
    }),
    speakable: {
      cssSelector: [".article-headline", ".speakable-intro"],
    },
    mainEntityOfPage: `${URL_ORIGIN}${removeLeadingSlash(article.urlPath || "")}`,
  };

  return {
    breadcrumbItems,
    articleSchemaProps,
  };
}
